/* Advisor Product Dashboard — Release 0.8
 *
 * Panel ejecutivo del Habita Advisor. Diseñado para responder en <30 seg
 * cuatro preguntas de negocio:
 *   1. ¿Las personas usan el Advisor?
 *   2. ¿El Advisor ayuda a progresar?
 *   3. ¿El Advisor genera negocio?
 *   ★ ¿Cómo va nuestra North Star Metric?
 *
 * No es un dashboard de datos — es una herramienta de decisión de producto.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
} from 'recharts';
import {
  TrendingUp, TrendingDown, Users, Compass, MessageCircle,
  BarChart3, Target, Clock, Building2, ArrowRight, RefreshCw,
} from 'lucide-react';
import Header from '../components/Header';
import ProductInsightsSection from '../components/advisor/ProductInsightsSection';
import { API_BASE } from '../lib/api';


const HABITA_GREEN = '#00824D';
const RANGES = [
  { key: '7d', label: '7 días' },
  { key: '30d', label: '30 días' },
  { key: '90d', label: '90 días' },
  { key: 'all', label: 'Todo' },
];


async function _fetchJson(path) {
  const token = localStorage.getItem('habita_token');
  const r = await fetch(`${API_BASE}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!r.ok) return null;
  return r.json();
}


function Delta({ pct }) {
  if (pct === null || pct === undefined) {
    return <span className="text-xs text-gray-400">— sin comparación</span>;
  }
  const positive = pct >= 0;
  const Icon = positive ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium ${positive ? 'text-emerald-600' : 'text-red-600'}`}>
      <Icon className="w-3 h-3" />
      {positive ? '+' : ''}{pct}% vs periodo anterior
    </span>
  );
}


function MetricCard({ icon: Icon, label, value, delta, testid }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-gray-500 mb-3">
        <Icon className="w-4 h-4 text-habita" />
        {label}
      </div>
      <p className="font-serif text-4xl font-bold text-habita-dark leading-none mb-2">
        {value ?? '—'}
      </p>
      {delta !== undefined && <Delta pct={delta} />}
    </div>
  );
}


function Section({ title, description, children, testid }) {
  return (
    <section className="mb-10" data-testid={testid}>
      <h2 className="font-serif text-2xl text-habita-dark font-bold">{title}</h2>
      {description && <p className="text-sm text-gray-500 mt-1 mb-5 max-w-2xl">{description}</p>}
      {children}
    </section>
  );
}


// ────────────────────────────────────────── North Star Card

function NorthStarCard({ data }) {
  if (!data) return <div className="animate-pulse h-56 bg-gray-100 rounded-3xl" />;
  const funnel = data.funnel || {};
  const stages = [
    { key: 'started', label: 'Iniciaron' },
    { key: 'exploring', label: 'Explorando' },
    { key: 'focusing', label: 'Enfocando' },
    { key: 'comparing', label: 'Comparando' },
    { key: 'first_contact', label: 'Primer contacto' },
  ];
  const maxVal = Math.max(...stages.map((s) => funnel[s.key] || 0), 1);
  return (
    <div
      className="rounded-3xl bg-gradient-to-br from-habita-dark via-habita to-emerald-700 text-white p-6 sm:p-8 shadow-xl"
      data-testid="dashboard-north-star"
    >
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-habita-mint mb-2">
            <Target className="w-4 h-4" />
            North Star Metric
          </div>
          <p className="font-serif text-6xl font-bold mb-2" data-testid="north-star-value">
            {data.value}
          </p>
          <p className="text-sm text-habita-mint/90 max-w-md">
            Usuarios que llegaron a <span className="font-semibold text-white">Comparando</span> y luego dieron su primer contacto sobre una propiedad.
          </p>
          {data.delta_pct !== null && (
            <p className="mt-3 text-sm">
              {data.delta_pct >= 0 ? '↑' : '↓'} {data.delta_pct}% vs. periodo anterior
              <span className="text-habita-mint/70"> (era {data.previous_value})</span>
            </p>
          )}
        </div>
        {/* Sparkline tendencia 30d */}
        <div className="w-full sm:w-64 h-24" data-testid="north-star-trend">
          <ResponsiveContainer width="100%" height="100%" minWidth={100} minHeight={80}>
            <LineChart data={data.trend_30d}>
              <Line type="monotone" dataKey="value" stroke="#DFF7E8" strokeWidth={2} dot={false} />
              <Tooltip
                contentStyle={{ background: 'rgba(0,0,0,.7)', border: 'none', borderRadius: 8, color: 'white', fontSize: 12 }}
                formatter={(v) => [`${v} conversiones`, '']}
              />
            </LineChart>
          </ResponsiveContainer>
          <p className="text-xs text-habita-mint/70 mt-1">Últimos 30 días</p>
        </div>
      </div>

      {/* Funnel */}
      <div className="mt-8 space-y-2" data-testid="north-star-funnel">
        {stages.map((s) => {
          const v = funnel[s.key] ?? 0;
          const pct = (v / maxVal) * 100;
          return (
            <div key={s.key} className="flex items-center gap-3">
              <span className="w-32 text-xs text-habita-mint/90">{s.label}</span>
              <div className="flex-1 h-6 bg-white/10 rounded-lg overflow-hidden">
                <div
                  className="h-full bg-habita-mint/70 rounded-lg transition-all"
                  style={{ width: `${Math.max(3, pct)}%` }}
                  data-testid={`funnel-bar-${s.key}`}
                />
              </div>
              <span className="w-12 text-right font-serif text-lg font-bold">{v}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ────────────────────────────────────────── Distribution bar (used by Progress)

function DistributionBar({ data, keys, keyLabels, testid }) {
  const total = keys.reduce((sum, k) => sum + (data[k] || 0), 0) || 1;
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid={testid}>
      {keys.map((k) => {
        const v = data[k] || 0;
        const pct = Math.round((v / total) * 100);
        return (
          <div key={k} className="mb-3 last:mb-0">
            <div className="flex justify-between text-sm text-habita-dark mb-1">
              <span>{keyLabels[k] || k}</span>
              <span className="font-medium">{v} <span className="text-gray-400">({pct}%)</span></span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full bg-habita transition-all" style={{ width: `${pct}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}


// ────────────────────────────────────────── Page

export default function AdvisorProductDashboard() {
  const [range, setRange] = useState('30d');
  const [northStar, setNorthStar] = useState(null);
  const [adoption, setAdoption] = useState(null);
  const [progress, setProgress] = useState(null);
  const [business, setBusiness] = useState(null);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (r) => {
    setLoading(true);
    try {
      const [ns, ad, pr, bu, ins] = await Promise.all([
        _fetchJson(`/advisor/admin/dashboard/north-star?range=${r}`),
        _fetchJson(`/advisor/admin/dashboard/adoption?range=${r}`),
        _fetchJson(`/advisor/admin/dashboard/progress?range=${r}`),
        _fetchJson(`/advisor/admin/dashboard/business?range=${r}`),
        _fetchJson(`/advisor/admin/dashboard/insights?range=${r}`),
      ]);
      setNorthStar(ns);
      setAdoption(ad);
      setProgress(pr);
      setBusiness(bu);
      setInsights(ins?.insights ?? []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(range); }, [range]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10" data-testid="advisor-dashboard-page">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
          <div>
            <p className="text-xs uppercase tracking-widest text-habita/70 font-semibold">Habita Advisor</p>
            <h1 className="font-serif text-3xl sm:text-4xl text-habita-dark font-bold">Dashboard de Producto</h1>
            <p className="text-sm text-gray-500 mt-1 max-w-2xl">
              Responde en 30 segundos: ¿el Advisor está ayudando a las personas a decidir?
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap" data-testid="range-selector">
            {RANGES.map((r) => (
              <button
                key={r.key}
                onClick={() => setRange(r.key)}
                data-testid={`range-${r.key}`}
                className={`px-3 h-8 rounded-full border text-sm ${
                  range === r.key
                    ? 'bg-habita text-white border-habita'
                    : 'bg-white border-gray-200 text-gray-600 hover:border-habita'
                }`}
              >
                {r.label}
              </button>
            ))}
            <button
              onClick={() => load(range)}
              disabled={loading}
              className="p-2 rounded-full border border-emerald-600 text-habita-dark hover:bg-habita hover:text-white disabled:opacity-50 transition-colors"
              aria-label="Refrescar"
              data-testid="refresh-button"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* NORTH STAR — hero card */}
        <div className="mb-10">
          <NorthStarCard data={northStar} />
        </div>

        {/* Product Insights (Milestone 1) — qué merece atención */}
        <ProductInsightsSection insights={insights} loading={loading} />

        {/* Q1 · Adopción */}
        <Section
          title="¿Las personas usan el Advisor?"
          description="Cuántos usuarios inician un proceso y llegan a las herramientas clave."
          testid="section-adoption"
        >
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard icon={Users}          label="Contextos iniciados" value={adoption?.contexts_started?.value}     delta={adoption?.contexts_started?.delta_pct}     testid="metric-contexts-started" />
            <MetricCard icon={Compass}        label='Llegaron a "Mi decisión"' value={adoption?.workspace_reached?.value} delta={adoption?.workspace_reached?.delta_pct} testid="metric-workspace-reached" />
            <MetricCard icon={MessageCircle}  label="Respondieron al Companion" value={adoption?.companion_interacted?.value} delta={adoption?.companion_interacted?.delta_pct} testid="metric-companion-interacted" />
            <MetricCard icon={BarChart3}      label="Usaron el Comparador"  value={adoption?.comparator_used?.value}    delta={adoption?.comparator_used?.delta_pct}    testid="metric-comparator-used" />
          </div>
        </Section>

        {/* Q2 · Progresión */}
        <Section
          title="¿El Advisor ayuda a progresar?"
          description="La sección más importante — cómo avanzan los usuarios a través del proceso."
          testid="section-progress"
        >
          <div className="grid md:grid-cols-3 gap-4">
            <MetricCard
              icon={ArrowRight}
              label="Usuarios que avanzaron"
              value={progress?.user_progressed_count}
              testid="metric-user-progressed"
            />
            <DistributionBar
              data={progress?.readiness_distribution || {}}
              keys={['exploring', 'focusing', 'comparing', 'ready']}
              keyLabels={{
                exploring: 'Explorando', focusing: 'Enfocando',
                comparing: 'Comparando', ready: 'Listo para decidir',
              }}
              testid="dist-readiness"
            />
            <DistributionBar
              data={progress?.momentum_distribution || {}}
              keys={['growing', 'stable', 'stalled']}
              keyLabels={{
                growing: 'Avanzando', stable: 'Ritmo constante', stalled: 'Pausado',
              }}
              testid="dist-momentum"
            />
          </div>
          {progress?.progression_transitions?.length > 0 && (
            <div className="mt-4 bg-white rounded-2xl border border-gray-200 p-5" data-testid="progression-transitions">
              <p className="text-xs uppercase tracking-widest text-gray-500 mb-3">Transiciones más comunes</p>
              <ul className="space-y-2">
                {progress.progression_transitions.slice(0, 5).map((t, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm text-habita-dark">
                    <span className="capitalize">{t.from || 'inicio'}</span>
                    <ArrowRight className="w-3.5 h-3.5 text-habita" />
                    <span className="capitalize">{t.to}</span>
                    <span className="ml-auto text-gray-500">{t.count} usuarios</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Section>

        {/* Q3 · Negocio */}
        <Section
          title="¿El Advisor genera negocio?"
          description="Indicadores concretos de conversión y retorno de los usuarios."
          testid="section-business"
        >
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard
              icon={Building2}
              label="Contactos enviados"
              value={business?.contacts_sent?.value}
              delta={business?.contacts_sent?.delta_pct}
              testid="metric-contacts-sent"
            />
            <MetricCard
              icon={Clock}
              label="Tiempo mediano al 1er contacto"
              value={business?.median_time_to_first_contact_hours != null ? `${business.median_time_to_first_contact_hours} h` : '—'}
              testid="metric-median-hours"
            />
            <MetricCard
              icon={BarChart3}
              label="Comparaciones antes del 1er contacto"
              value={business?.comparisons_before_first_contact_avg != null ? business.comparisons_before_first_contact_avg : '—'}
              testid="metric-comparisons-before-contact"
            />
            <MetricCard
              icon={Compass}
              label='Vuelven a "Mi decisión"'
              value={business?.workspace_return_users?.value}
              delta={business?.workspace_return_users?.delta_pct}
              testid="metric-workspace-return"
            />
          </div>
        </Section>

        {/* Footer nav */}
        <div className="mt-10 pt-6 border-t border-gray-200 flex items-center justify-between flex-wrap gap-2">
          <p className="text-xs text-gray-400">
            Datos actualizados en tiempo real. Rango activo: <span className="font-medium">{RANGES.find((r) => r.key === range)?.label}</span>.
          </p>
          <Link
            to="/admin/advisor"
            className="text-sm inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border border-emerald-600 text-habita-dark font-semibold hover:bg-habita hover:text-white transition-colors"
            data-testid="link-technical-inspector"
          >
            Inspector técnico
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </main>
    </div>
  );
}
